import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrochip, faScrewdriverWrench, faPlug, faLayerGroup, faSliders,
  faBoxesStacked, faFileLines, faLink, faArrowUpRightFromSquare, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { getStatusMeta, statusExplanation } from '../../utils/compatibilityStatus';
import { useTranslation } from 'react-i18next';

const CONDITION_ICON = { firmware: faMicrochip, bms: faScrewdriverWrench, cable: faPlug, modules: faLayerGroup, setting: faSliders };

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

const Section = ({ title, children }) => (
  <div className="pt-4 mt-4 border-t border-gray-100">
    <h3 className="text-sm font-bold text-gray-900 mb-2.5">{title}</h3>
    {children}
  </div>
);

// Karta wyniku /check — renderuje wyłącznie to, co przyszło z API (§4 kontraktu).
// Status "unknown" musi być wizualnie i tekstowo neutralny — bez czerwieni, bez "nie".
const CompatibilityResultCard = ({ result }) => {
  const { t } = useTranslation('zgodnosc');
  if (!result) return null;
  const meta = getStatusMeta(result.user_status);
  const isUnknown = result.user_status === 'unknown';
  const isIncompatible = result.user_status === 'incompatible';
  const isConflicting = result.user_status === 'conflicting';

  const hasConditions = Array.isArray(result.conditions) && result.conditions.length > 0;
  const hasCapacity = !!result.capacity;
  const hasSources = Array.isArray(result.sources) && result.sources.length > 0;
  const hasLimitations = hasValue(result.limitations_pl);
  const hasOffers = result.offers && (hasValue(result.offers.inverter_offers_url) || hasValue(result.offers.battery_offers_url));
  // Przy "conflicting" limitations_pl to GŁÓWNA treść karty (ANEKS A4) — pokazana
  // w nagłówku, więc nie duplikujemy jej niżej w sekcji "Ograniczenia".
  const conflictingText = hasLimitations ? result.limitations_pl : t('sprzeczne_domyslnie');

  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-2xl border ${meta.tone} p-5 md:p-7`}
    >
      {/* Nagłówek statusu — kolor NIGDY nie jest jedynym nośnikiem informacji: ikona + etykieta tekstowa zawsze obecne. */}
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={meta.icon} className={`text-2xl mt-0.5 ${meta.iconTone}`} aria-hidden="true" />
        <div className="min-w-0">
          <h2 className="text-lg font-bold">{meta.label}</h2>
          {(isUnknown || isIncompatible) && (
            <p className="mt-1.5 text-sm leading-6">{statusExplanation(result.user_status, result.reason_code)}</p>
          )}
          {isConflicting && (
            <p className="mt-1.5 text-sm leading-6 font-medium">{conflictingText}</p>
          )}
        </div>
      </div>

      {/* Strona tej pary (/kompatybilnosc/<slug>): pełny opis reguły i wszystkie modele obu stron.
          Zwykły link, bo stronę renderuje backend (trasa /kompatybilnosc/:slug przez nginx). */}
      {hasValue(result.pair_page_url) && (
        <p className="mt-3 text-sm">
          <a href={result.pair_page_url} className="inline-flex items-center gap-1.5 font-semibold text-blue-700 hover:underline">
            {t('strona_pary', { para: result.pair_page_name || `${result.inverter?.manufacturer || ''} + ${result.battery?.manufacturer || ''}` })}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
          </a>
        </p>
      )}

      {result.requires_manual_review && (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 bg-white/60 rounded-lg px-3 py-2 border border-current/10">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-500" />
          {t('zalecana_weryfikacja')}
        </div>
      )}

      {/* Warunki kompatybilności — zawsze widoczne bez rozwijania (kluczowe dla "conditional"). */}
      {hasConditions && (
        <Section title={t('warunki')}>
          <ul className="space-y-2.5">
            {result.conditions.map((c, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                <FontAwesomeIcon icon={CONDITION_ICON[c.type] || faSliders} className="mt-0.5 text-blue-500 shrink-0" />
                <span><span className="font-semibold">{c.label_pl}:</span> {c.value_pl}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Pojemność i moduły — rozróżnienie wieża vs. całość to najczęstsze źródło pomyłek. */}
      {hasCapacity && (
        <Section title={t('pojemnosc_i_moduly')}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            {hasValue(result.capacity.module_capacity_kwh) && (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('pojemnosc_modulu')}</p><p className="mt-1 text-sm font-semibold text-gray-800">{result.capacity.module_capacity_kwh} kWh</p></div>
            )}
            {hasValue(result.capacity.min_modules) && (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('min_modulow')}</p><p className="mt-1 text-sm font-semibold text-gray-800">{result.capacity.min_modules}</p></div>
            )}
            {hasValue(result.capacity.max_modules_per_tower) && (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('max_modulow_wieza')}</p><p className="mt-1 text-sm font-semibold text-gray-800">{result.capacity.max_modules_per_tower}</p></div>
            )}
            {hasValue(result.capacity.max_capacity_per_tower_kwh) && (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('pojemnosc_wiezy')}</p><p className="mt-1 text-sm font-semibold text-gray-800">{result.capacity.max_capacity_per_tower_kwh} kWh</p></div>
            )}
            {hasValue(result.capacity.max_parallel_towers) ? (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('wiez_rownolegle')}</p><p className="mt-1 text-sm font-semibold text-gray-800">{result.capacity.max_parallel_towers}</p></div>
            ) : (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('wiez_rownolegle')}</p><p className="mt-1 text-sm font-semibold text-gray-400">{t('nie_potwierdzono_wiez')}</p></div>
            )}
            {hasValue(result.capacity.max_total_capacity_kwh) && (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide">{t('pojemnosc_calkowita')}</p><p className="mt-1 text-sm font-semibold text-gray-800">{result.capacity.max_total_capacity_kwh} kWh</p></div>
            )}
          </div>
          {hasValue(result.capacity.note_pl) && <p className="mt-3 text-xs text-gray-500">{result.capacity.note_pl}</p>}
        </Section>
      )}

      {/* Wymagany BMS/kontroler — osobno wyeksponowany, łatwo pominąć przy budżetowaniu. */}
      {hasValue(result.required_bms) && (
        <Section title={t('wymagany_bms')}>
          <div className="flex items-center gap-2.5 text-sm text-gray-700 bg-white/60 rounded-lg px-3 py-2.5 border border-current/10">
            <FontAwesomeIcon icon={faBoxesStacked} className="text-blue-500" />
            <span className="font-semibold">{result.required_bms}</span>
            {hasValue(result.communication_protocol) && <span className="text-gray-400">{t('protokol', { protokol: result.communication_protocol })}</span>}
          </div>
        </Section>
      )}

      {/* Ograniczenia — wyróżnione jako ostrzeżenie. Pominięte przy "incompatible" (powód
          jest już w nagłówku) i przy "conflicting" (limitations_pl już pokazane wyżej jako główna treść). */}
      {hasLimitations && !isIncompatible && !isConflicting && (
        <Section title={t('ograniczenia')}>
          <div className="flex items-start gap-2.5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-600 mt-0.5 shrink-0" />
            <span>{result.limitations_pl}</span>
          </div>
        </Section>
      )}

      {/* Źródło i data weryfikacji — buduje wiarygodność, nie chowamy w stopce. */}
      {hasSources && (
        <Section title={isConflicting ? t('zrodla_rozne') : t('zrodlo_i_data')}>
          <ul className="space-y-2.5">
            {result.sources.map((s) => (
              <li key={s.source_id} className="flex items-start gap-2.5 text-sm text-gray-700">
                <FontAwesomeIcon icon={faFileLines} className="mt-0.5 text-gray-400 shrink-0" />
                <span>
                  <span className="font-semibold">{s.publisher}</span>{hasValue(s.title) && <>: {s.title}</>}
                  {hasValue(s.publication_date) && <span className="text-gray-400"> ({s.publication_date})</span>}
                  {hasValue(s.url) && (
                    <>
                      {' '}
                      <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 hover:underline whitespace-nowrap">
                        {t('zobacz_dokument')} <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                      </a>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {hasValue(result.last_verified) && <p className="mt-2.5 text-xs text-gray-400">{t('ostatnia_weryfikacja', { data: result.last_verified })}</p>}
        </Section>
      )}

      {/* Oferty — wynik ma prowadzić do produktu, nie kończyć się komunikatem technicznym. */}
      {hasOffers && (
        <Section title={t('oferty')}>
          <div className="flex flex-col sm:flex-row gap-2.5">
            {hasValue(result.offers.inverter_offers_url) && (
              <a href={result.offers.inverter_offers_url} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors">
                <FontAwesomeIcon icon={faLink} /> {t('oferty_falownika')}
              </a>
            )}
            {hasValue(result.offers.battery_offers_url) && (
              <a href={result.offers.battery_offers_url} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors">
                <FontAwesomeIcon icon={faLink} /> {t('oferty_magazynu')}
              </a>
            )}
          </div>
        </Section>
      )}
    </div>
  );
};

export default CompatibilityResultCard;
